import { useState } from 'react';
import {
  App as AntApp,
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
} from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined, ThunderboltOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import {
  useCreateSmsRule,
  useCreateSmsTemplate,
  useDeleteSmsRule,
  useDeleteSmsTemplate,
  useUpdateSmsRule,
  useUpdateSmsTemplate,
  useSmsMessages,
  useSmsRuleDryRun,
  useSmsRules,
  useSmsTemplates,
  type SmsMessage,
  type SmsRule,
  type SmsStatus,
  type SmsTemplate,
  type SmsTriggerType,
} from '../api/sms';
import { asApiError } from '@/shared/lib/api';
import { RoleGate, ADMIN_ROLES } from '@/shared/auth/role';

const STATUS_COLOR: Record<SmsStatus, string> = {
  queued: 'default',
  sent: 'blue',
  delivered: 'green',
  failed: 'red',
  undelivered: 'volcano',
};

const TRIGGERS: SmsTriggerType[] = ['days_before_due', 'days_after_due', 'on_event', 'manual'];

const TEMPLATE_VARS = [
  { key: '{{customer.firstName}}',      i18n: 'sms.varFirstName' },
  { key: '{{customer.lastName}}',       i18n: 'sms.varLastName' },
  { key: '{{customer.middleName}}',     i18n: 'sms.varMiddleName' },
  { key: '{{customer.phone}}',          i18n: 'sms.varPhone' },
  { key: '{{contract.contractNumber}}',   i18n: 'sms.varContractNumber' },
  { key: '{{contract.totalAmount}}',      i18n: 'sms.varTotalAmount' },
  { key: '{{schedule.dueDate}}',          i18n: 'sms.varDueDate' },
  { key: '{{schedule.amountDue}}',        i18n: 'sms.varAmountDue' },
  { key: '{{payment.amount}}',            i18n: 'sms.varPaymentAmount' },
  { key: '{{payment.remainingBalance}}',  i18n: 'sms.varRemainingBalance' },
] as const;

export function SmsHubPage() {
  const { t } = useTranslation();

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Typography.Title level={3} style={{ margin: 0 }}>{t('sms.title')}</Typography.Title>
      <Tabs
        items={[
          { key: 'templates', label: t('sms.tabs.templates'), children: <TemplatesTab /> },
          { key: 'rules', label: t('sms.tabs.rules'), children: <RulesTab /> },
          { key: 'log', label: t('sms.tabs.log'), children: <LogTab /> },
        ]}
      />
    </Space>
  );
}

function TemplatesTab() {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const { data } = useSmsTemplates();
  const create = useCreateSmsTemplate();
  const update = useUpdateSmsTemplate();
  const remove = useDeleteSmsTemplate();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<SmsTemplate | null>(null);
  const [bodyTab, setBodyTab] = useState('uz');
  const [form] = Form.useForm();

  function handleOpen() { setEditing(null); setOpen(true); setBodyTab('uz'); }

  function handleEdit(record: SmsTemplate) {
    setEditing(record);
    form.setFieldsValue({ name: record.name, bodyUz: record.bodyUz, bodyRu: record.bodyRu ?? '', bodyEn: record.bodyEn ?? '' });
    setBodyTab('uz');
    setOpen(true);
  }

  function handleCancel() { setOpen(false); setEditing(null); form.resetFields(); setBodyTab('uz'); }

  async function onOk() {
    try {
      const v = await form.validateFields();
      if (editing) {
        await update.mutateAsync({ id: editing.id, input: v });
        void message.success(t('sms.templateUpdated'));
      } else {
        await create.mutateAsync(v);
        void message.success(t('sms.templateCreated'));
      }
      handleCancel();
    } catch (err) {
      const fe = (err as { errorFields?: { name: string[] }[] }).errorFields;
      if (fe) {
        if (fe.some((f) => f.name.includes('bodyUz'))) setBodyTab('uz');
        else if (fe.some((f) => f.name.includes('bodyRu'))) setBodyTab('ru');
        else if (fe.some((f) => f.name.includes('bodyEn'))) setBodyTab('en');
        return;
      }
      void message.error(asApiError(err).message);
    }
  }

  return (
    <>
      <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'flex-end' }}>
        <RoleGate roles={ADMIN_ROLES}>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleOpen}>
            {t('sms.newTemplate')}
          </Button>
        </RoleGate>
      </div>
      <Card bodyStyle={{ padding: 0 }}>
        <Table<SmsTemplate>
          rowKey="id"
          dataSource={data ?? []}
          pagination={false}
          columns={[
            { title: t('sms.name'), dataIndex: 'name', width: 220 },
            { title: t('sms.bodyUz'), dataIndex: 'bodyUz', ellipsis: true },
            {
              title: t('sms.moderation'),
              dataIndex: 'moderationStatus',
              width: 130,
              render: (s: string) => <Tag>{t(`sms.moderationStatus.${s}`)}</Tag>,
            },
            { title: t('sms.active'), dataIndex: 'active', width: 80, render: (v: boolean) => <Tag color={v ? 'green' : 'default'}>{v ? '●' : '—'}</Tag> },
            {
              title: '',
              width: 100,
              render: (_: unknown, record: SmsTemplate) => (
                <RoleGate roles={ADMIN_ROLES}>
                <Space size={4}>
                  <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
                  <Popconfirm
                    title={t('sms.deleteConfirm')}
                    okText={t('common.delete')}
                    okButtonProps={{ danger: true }}
                    cancelText={t('common.cancel')}
                    onConfirm={() => {
                      void remove.mutateAsync(record.id)
                        .then(() => void message.success(t('sms.templateDeleted')))
                        .catch((err) => void message.error(asApiError(err).message));
                    }}
                  >
                    <Button size="small" icon={<DeleteOutlined />} danger />
                  </Popconfirm>
                </Space>
                </RoleGate>
              ),
            },
          ]}
        />
      </Card>

      <Modal
        title={editing ? t('sms.editTemplateTitle') : t('sms.newTemplateTitle')}
        open={open}
        onOk={onOk}
        onCancel={handleCancel}
        confirmLoading={create.isPending || update.isPending}
        okText={editing ? t('common.save') : t('common.create')}
        cancelText={t('common.cancel')}
        destroyOnHidden
        width={580}
      >
        <Form form={form} layout="vertical" requiredMark={false} initialValues={{ active: true }}>
          <Form.Item name="name" label={t('sms.name')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>

          <Tabs
            activeKey={bodyTab}
            onChange={setBodyTab}
            size="small"
            items={[
              {
                key: 'uz',
                label: <span>UZ <span style={{ color: '#ff4d4f' }}>*</span></span>,
                children: (
                  <Form.Item name="bodyUz" rules={[{ required: true, min: 10 }]} style={{ marginBottom: 0 }}>
                    <Input.TextArea rows={5} maxLength={800} showCount autoSize={{ minRows: 5, maxRows: 10 }} />
                  </Form.Item>
                ),
              },
              {
                key: 'ru',
                label: 'RU',
                children: (
                  <Form.Item name="bodyRu" rules={[{ min: 10 }]} style={{ marginBottom: 0 }}>
                    <Input.TextArea rows={5} maxLength={800} showCount autoSize={{ minRows: 5, maxRows: 10 }} />
                  </Form.Item>
                ),
              },
              {
                key: 'en',
                label: 'EN',
                children: (
                  <Form.Item name="bodyEn" rules={[{ min: 10 }]} style={{ marginBottom: 0 }}>
                    <Input.TextArea rows={5} maxLength={800} showCount autoSize={{ minRows: 5, maxRows: 10 }} />
                  </Form.Item>
                ),
              },
            ]}
          />

          <div style={{ marginTop: 12, padding: '10px 12px', background: '#fafafa', borderRadius: 6, border: '1px solid #f0f0f0' }}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {t('sms.bodyFallbackHint')}
            </Typography.Text>
            <div style={{ marginTop: 8 }}>
              <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 6 }}>
                {t('sms.availableVars')}:
              </Typography.Text>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {TEMPLATE_VARS.map((v) => (
                  <div key={v.key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Typography.Text code copyable style={{ fontSize: 11, flex: '0 0 auto' }}>
                      {v.key}
                    </Typography.Text>
                    <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                      — {t(v.i18n)}
                    </Typography.Text>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Form>
      </Modal>
    </>
  );
}

function RulesTab() {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const { data } = useSmsRules();
  const { data: templates } = useSmsTemplates();
  const create = useCreateSmsRule();
  const update = useUpdateSmsRule();
  const remove = useDeleteSmsRule();
  const dryRun = useSmsRuleDryRun();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<SmsRule | null>(null);
  const [form] = Form.useForm();

  function handleOpen() { setEditing(null); setOpen(true); }

  function handleEdit(record: SmsRule) {
    setEditing(record);
    form.setFieldsValue({
      name: record.name,
      templateId: record.templateId,
      triggerType: record.triggerType,
      eventCode: record.eventCode ?? undefined,
      daysOffset: record.daysOffset ?? undefined,
      active: record.active,
    });
    setOpen(true);
  }

  function handleCancel() { setOpen(false); setEditing(null); form.resetFields(); }

  async function onOk() {
    try {
      const v = await form.validateFields();
      if (editing) {
        await update.mutateAsync({ id: editing.id, input: v });
        void message.success(t('sms.ruleUpdated'));
      } else {
        await create.mutateAsync(v);
        void message.success(t('sms.ruleCreated'));
      }
      handleCancel();
    } catch (err) {
      if ((err as { errorFields?: unknown[] }).errorFields) return;
      void message.error(asApiError(err).message);
    }
  }

  async function handleDryRun(id: string) {
    try {
      const r = await dryRun.mutateAsync(id);
      void message.info(`${t('sms.dryRunRecipients')}: ${r.recipientCount}${r.note ? ` · ${r.note}` : ''}`);
    } catch (err) {
      void message.error(asApiError(err).message);
    }
  }

  return (
    <>
      <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'flex-end' }}>
        <RoleGate roles={ADMIN_ROLES}>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleOpen}>
            {t('sms.newRule')}
          </Button>
        </RoleGate>
      </div>
      <Card bodyStyle={{ padding: 0 }}>
        <Table<SmsRule>
          rowKey="id"
          dataSource={data ?? []}
          pagination={false}
          columns={[
            { title: t('sms.name'), dataIndex: 'name' },
            { title: t('sms.trigger'), dataIndex: 'triggerType', width: 180, render: (v: SmsTriggerType) => t(`sms.triggers.${v}`) },
            { title: t('sms.daysOffset'), dataIndex: 'daysOffset', width: 90 },
            { title: t('sms.template'), width: 200, render: (_: unknown, r: SmsRule) => r.template?.name ?? '—' },
            { title: t('sms.active'), dataIndex: 'active', width: 80, render: (v: boolean) => <Tag color={v ? 'green' : 'default'}>{v ? '●' : '—'}</Tag> },
            {
              title: '',
              width: 160,
              render: (_: unknown, r: SmsRule) => (
                <Space size={4}>
                  <Button size="small" icon={<ThunderboltOutlined />} onClick={() => handleDryRun(r.id)}>
                    {t('sms.dryRun')}
                  </Button>
                  <RoleGate roles={ADMIN_ROLES}>
                    <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(r)} />
                  </RoleGate>
                  <RoleGate roles={ADMIN_ROLES}>
                    <Popconfirm
                      title={t('sms.ruleDeleteConfirm')}
                      okText={t('common.delete')}
                      okButtonProps={{ danger: true }}
                      cancelText={t('common.cancel')}
                      onConfirm={() => {
                        void remove.mutateAsync(r.id)
                          .then(() => void message.success(t('sms.ruleDeleted')))
                          .catch((err) => void message.error(asApiError(err).message));
                      }}
                    >
                      <Button size="small" icon={<DeleteOutlined />} danger />
                    </Popconfirm>
                  </RoleGate>
                </Space>
              ),
            },
          ]}
        />
      </Card>

      <Modal
        title={editing ? t('sms.editRuleTitle') : t('sms.newRuleTitle')}
        open={open}
        onOk={onOk}
        onCancel={handleCancel}
        confirmLoading={create.isPending || update.isPending}
        okText={editing ? t('common.save') : t('common.create')}
        cancelText={t('common.cancel')}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" requiredMark={false} initialValues={{ triggerType: 'days_before_due', active: true, critical: false }}>
          <Form.Item name="name" label={t('sms.name')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="templateId" label={t('sms.template')} rules={[{ required: true }]}>
            <Select options={(templates ?? []).map((tt) => ({ value: tt.id, label: tt.name }))} />
          </Form.Item>
          <Form.Item name="triggerType" label={t('sms.trigger')}>
            <Select options={TRIGGERS.map((tr) => ({ value: tr, label: t(`sms.triggers.${tr}`) }))} />
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.triggerType !== cur.triggerType}>
            {({ getFieldValue }) => {
              const type = getFieldValue('triggerType') as SmsTriggerType;
              if (type === 'on_event') return (
                <Form.Item name="eventCode" label={t('sms.eventCode')} rules={[{ required: true }]}
                  extra={
                    <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                      contract.created · payment.received · contract.cancelled
                    </Typography.Text>
                  }
                >
                  <Select options={[
                    { value: 'contract.created',   label: t('sms.events.contractCreated') },
                    { value: 'payment.received',   label: t('sms.events.paymentReceived') },
                    { value: 'contract.cancelled', label: t('sms.events.contractCancelled') },
                  ]} />
                </Form.Item>
              );
              if (type === 'days_before_due' || type === 'days_after_due') return (
                <Form.Item name="daysOffset" label={t('sms.daysOffset')}>
                  <InputNumber style={{ width: '100%' }} min={0} max={180} />
                </Form.Item>
              );
              return null;
            }}
          </Form.Item>
          <Form.Item name="active" label={t('sms.active')}>
            <Select options={[{ value: true, label: t('common.yes') }, { value: false, label: t('common.no') }]} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}


function LogTab() {
  const { t } = useTranslation();
  const { data } = useSmsMessages();
  return (
    <Card bodyStyle={{ padding: 0 }}>
      <Table<SmsMessage>
        rowKey="id"
        dataSource={data ?? []}
        pagination={false}
        size="small"
        columns={[
          { title: t('sms.createdAt'), dataIndex: 'createdAt', width: 160, render: (v: string) => dayjs(v).format('DD.MM.YYYY HH:mm') },
          { title: t('sms.phone'), dataIndex: 'phone', width: 160 },
          { title: t('sms.body'), dataIndex: 'renderedBody', ellipsis: true },
          { title: t('sms.status'), dataIndex: 'status', width: 110, render: (s: SmsStatus) => <Tag color={STATUS_COLOR[s]}>{t(`sms.statuses.${s}`)}</Tag> },
          { title: t('sms.provider'), dataIndex: 'provider', width: 100, render: (v: string | null) => v ?? '—' },
          { title: t('sms.providerMessageId'), dataIndex: 'providerMessageId', ellipsis: true, render: (v: string | null) => v ?? '—' },
        ]}
      />
    </Card>
  );
}
